import type {
  ActionConfig,
  ActionGroupConfig,
} from "@smartsuite/automation-engine-api";

/**
 * Snapshot-side twin of stores/automation/utils/action-group-walk.ts:
 * recurses ActionGroupConfig (no UI hashes) through loop/branches/repeat.
 */
export const findConfigGroupByInstanceId = (
  groups: ActionGroupConfig[],
  instanceId: number,
): ActionGroupConfig | undefined => {
  for (const group of groups) {
    if (group.instanceId === instanceId) return group;
    if (group.config.oneofKind === "loop") {
      const found = findConfigGroupByInstanceId(
        group.config.loop.actionGroups,
        instanceId,
      );
      if (found !== undefined) return found;
    }
    if (group.config.oneofKind === "branches") {
      const { branches } = group.config;
      const nested = [
        ...branches.branches.map((b) => b.actionGroups),
        ...(branches.elseBranch !== undefined
          ? [branches.elseBranch.actionGroups]
          : []),
      ];
      for (const list of nested) {
        const found = findConfigGroupByInstanceId(list, instanceId);
        if (found !== undefined) return found;
      }
    }
    if (group.config.oneofKind === "repeat") {
      const found = findConfigGroupByInstanceId(
        group.config.repeat.actionGroups,
        instanceId,
      );
      if (found !== undefined) return found;
    }
  }
  return undefined;
};

/**
 * Finds an action config by its actionReference.instanceId anywhere in the
 * config tree (recursing loop/branches/repeat). Unlike
 * findConfigGroupByInstanceId (which matches action-GROUP ids), this matches
 * an individual action -- e.g. the find-records action a loop's record-list
 * source references, wherever it sits (top-level or nested inside a
 * Conditional Logic branch).
 */
export const findActionConfigByInstanceId = (
  groups: ActionGroupConfig[],
  actionInstanceId: number,
): ActionConfig | undefined => {
  for (const group of groups) {
    if (group.config.oneofKind === "actions") {
      const action = group.config.actions.actions.find(
        (candidate) =>
          candidate.actionReference?.instanceId === actionInstanceId,
      );
      if (action !== undefined) return action;
    }
    if (group.config.oneofKind === "loop") {
      const found = findActionConfigByInstanceId(
        group.config.loop.actionGroups,
        actionInstanceId,
      );
      if (found !== undefined) return found;
    }
    if (group.config.oneofKind === "branches") {
      const { branches } = group.config;
      const nested = [
        ...branches.branches.map((b) => b.actionGroups),
        ...(branches.elseBranch !== undefined
          ? [branches.elseBranch.actionGroups]
          : []),
      ];
      for (const list of nested) {
        const found = findActionConfigByInstanceId(list, actionInstanceId);
        if (found !== undefined) return found;
      }
    }
    if (group.config.oneofKind === "repeat") {
      const found = findActionConfigByInstanceId(
        group.config.repeat.actionGroups,
        actionInstanceId,
      );
      if (found !== undefined) return found;
    }
  }
  return undefined;
};
